import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { languageFlags } from '../i18n/translations';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';

const Header = ({ onNavigate }) => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'properties', label: t('nav.properties') },
    { id: 'services', label: t('nav.services') },
    { id: 'contact', label: t('nav.contact') }
  ];

  const languages = [
    { code: 'fr', name: 'Français' },
    { code: 'en', name: 'English' },
    { code: 'id', name: 'Indonesia' },
    { code: 'es', name: 'Español' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-sm py-4' 
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center space-x-3" data-testid="logo">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A227] to-[#B8922B] flex items-center justify-center">
              <span className="text-white font-bold text-lg">BL</span>
            </div>
            <div>
              <h1 className={`font-playfair text-lg font-semibold tracking-wide ${isScrolled ? 'text-[#1A1A2E]' : 'text-white'}`}>
                Bali Life
              </h1>
              <p className="text-[#C9A227] text-[10px] tracking-[0.2em] uppercase">
                Concierge & Real Estate
              </p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-10">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={`hover:text-[#C9A227] transition-colors text-sm font-medium tracking-wide ${
                  isScrolled ? 'text-[#4A4A5A]' : 'text-white/90'
                }`}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-full border transition-all ${
                  isScrolled 
                    ? 'border-[#1A1A2E]/10 hover:border-[#C9A227]/50' 
                    : 'border-white/30 hover:border-white/50'
                }`}
                data-testid="language-selector"
              >
                <Globe className={`w-4 h-4 ${isScrolled ? 'text-[#C9A227]' : 'text-[#C9A227]'}`} />
                <span className={`text-sm hidden sm:inline ${isScrolled ? 'text-[#1A1A2E]' : 'text-white'}`}>
                  {languageFlags[language]}
                </span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isLangOpen ? 'rotate-180' : ''} ${isScrolled ? 'text-[#4A4A5A]' : 'text-white/60'}`} />
              </button>

              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl border border-gray-100 overflow-hidden shadow-xl">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsLangOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-[#F5F3EF] transition-colors ${
                        language === lang.code ? 'bg-[#F5F3EF] text-[#C9A227]' : 'text-[#1A1A2E]'
                      }`}
                      data-testid={`lang-${lang.code}`}
                    >
                      <span>{languageFlags[lang.code]}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Button */}
            <button
              onClick={() => onNavigate('contact')}
              className="hidden md:block btn-gold text-sm"
              data-testid="header-cta"
            >
              {t('nav.contact')}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 ${isScrolled ? 'text-[#1A1A2E]' : 'text-white'}`}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-6 pt-6 border-t border-gray-200 bg-white rounded-xl p-4 shadow-lg">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    onNavigate(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-[#4A4A5A] hover:text-[#C9A227] transition-colors text-left py-2"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => {
                  onNavigate('contact');
                  setIsMobileMenuOpen(false);
                }}
                className="btn-gold text-center mt-4"
              >
                {t('nav.contact')}
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
